import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { tenantService, type TenantDto } from '@/services/tenants';
import { Link } from 'react-router-dom';
import {
  superadminBillingService,
  type PaygUsageReportResult,
  type PaymentWebhookEventRow,
} from '@/services/superadminBilling';
import { useToast } from '@/components/ui/use-toast';
import { CreditCard, Layers, RotateCcw, BarChart3, RefreshCw, Activity } from 'lucide-react';

const SuperadminTenantPaymentsPage: React.FC = () => {
  const { toast } = useToast();
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(
    sessionStorage.getItem('selectedTenantId') || localStorage.getItem('selectedTenantId')
  );
  const [selectedTenant, setSelectedTenant] = useState<TenantDto | null>(null);
  const [paygRunning, setPaygRunning] = useState(false);
  const [paygResult, setPaygResult] = useState<PaygUsageReportResult | null>(null);
  const [webhooks, setWebhooks] = useState<PaymentWebhookEventRow[]>([]);
  const [webhooksLoading, setWebhooksLoading] = useState(false);
  const [reprocessBusy, setReprocessBusy] = useState<string | null>(null);

  const loadWebhooks = async () => {
    try {
      setWebhooksLoading(true);
      setWebhooks(await superadminBillingService.listPaymentWebhooks(25));
    } catch {
      toast({ title: 'Error', description: 'Failed to load webhooks', variant: 'destructive' });
    } finally {
      setWebhooksLoading(false);
    }
  };

  useEffect(() => {
    const tenantId = sessionStorage.getItem('selectedTenantId') || localStorage.getItem('selectedTenantId');
    setSelectedTenantId(tenantId);
    void loadWebhooks();
  }, []);

  useEffect(() => {
    const loadTenant = async () => {
      if (!selectedTenantId) return;
      try {
        const tenant = await tenantService.getTenant(selectedTenantId);
        setSelectedTenant(tenant);
      } catch {
        setSelectedTenant(null);
      }
    };
    loadTenant();
  }, [selectedTenantId]);

  const runPaygReport = async () => {
    setPaygRunning(true);
    setPaygResult(null);
    try {
      const result = await superadminBillingService.runPaygUsageReport();
      setPaygResult(result);
      toast({
        title: 'PAYG report finished',
        description: `${result.usageRecordsSent} usage record(s) sent · ${result.skipped} skipped · ${result.tenantsConsidered} tenant(s) considered`,
      });
    } catch (err: unknown) {
      const msg =
        err &&
        typeof err === 'object' &&
        'response' in err &&
        err.response &&
        typeof err.response === 'object' &&
        'data' in err.response &&
        err.response.data &&
        typeof err.response.data === 'object' &&
        'error' in err.response.data
          ? String((err.response.data as { error?: string }).error)
          : 'PAYG usage report failed';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setPaygRunning(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Tenant Payments - Arrangelly Dashboard</title>
      </Helmet>
      <DashboardLayout>
        <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Payments
              </CardTitle>
              <CardDescription>
                Platform billing tools for superadmin (SaaS plans, refunds, PAYG usage).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <Link
                  to="/tenants/billing-activity"
                  className="rounded-lg border border-slate-200 p-4 hover:border-violet-300 transition-colors"
                >
                  <p className="font-medium text-foreground flex items-center gap-2">
                    <Activity className="w-4 h-4" />
                    Billing activity
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Webhooks, DB before/after, credits &amp; refunds per tenant / student
                  </p>
                </Link>
                <Link
                  to="/tenants/saas-plans"
                  className="rounded-lg border border-slate-200 p-4 hover:border-violet-300 transition-colors"
                >
                  <p className="font-medium text-foreground flex items-center gap-2">
                    <Layers className="w-4 h-4" />
                    SaaS plans
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Teacher PAYG / monthly plans and Lemon variant IDs
                  </p>
                </Link>
                <Link
                  to="/tenants/refunds"
                  className="rounded-lg border border-slate-200 p-4 hover:border-violet-300 transition-colors"
                >
                  <p className="font-medium text-foreground flex items-center gap-2">
                    <RotateCcw className="w-4 h-4" />
                    Refund requests
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Student 14-day refund queue (process in Lemon, then mark here)
                  </p>
                </Link>
              </div>

              {selectedTenantId && (
                <p className="text-gray-400 text-xs">
                  Selected tenant (context): {selectedTenant?.name ?? selectedTenantId}
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2 text-base">
                <BarChart3 className="w-5 h-5" />
                PAYG usage report
              </CardTitle>
              <CardDescription>
                Push unreported student payment usage to Lemon Squeezy for all PAYG tenants. Run after
                staging/production deploy or on a schedule.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button onClick={() => void runPaygReport()} disabled={paygRunning}>
                <RefreshCw className={`w-4 h-4 mr-2 ${paygRunning ? 'animate-spin' : ''}`} />
                {paygRunning ? 'Reporting…' : 'Run PAYG usage report now'}
              </Button>

              {paygResult && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm space-y-2">
                  <p>
                    <span className="font-medium">Tenants considered:</span> {paygResult.tenantsConsidered}
                  </p>
                  <p>
                    <span className="font-medium">Usage records sent:</span> {paygResult.usageRecordsSent}
                  </p>
                  <p>
                    <span className="font-medium">Skipped:</span> {paygResult.skipped}
                  </p>
                  {paygResult.messages.length > 0 && (
                    <ul className="text-xs text-slate-600 list-disc pl-4 space-y-1">
                      {paygResult.messages.map((m, i) => (
                        <li key={i}>{m}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-foreground text-base">Lemon webhooks (staging debug)</CardTitle>
              <CardDescription>
                Recent deliveries stored in DB. Reprocess to replay credit grants after fixing config.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button variant="outline" size="sm" onClick={() => void loadWebhooks()} disabled={webhooksLoading}>
                <RefreshCw className={`w-4 h-4 mr-1 ${webhooksLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              {webhooks.length === 0 ? (
                <p className="text-xs text-slate-500">No webhook events yet.</p>
              ) : (
                <ul className="text-xs space-y-2 max-h-64 overflow-y-auto">
                  {webhooks.map((w) => (
                    <li key={w.id} className="border border-slate-200 rounded p-2">
                      <div className="font-medium">{w.eventName}</div>
                      <div className="text-slate-500">
                        {new Date(w.createdAt).toLocaleString()}
                        {w.processError ? ` · err: ${w.processError}` : ''}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-1 h-7 text-xs"
                        disabled={reprocessBusy === w.id}
                        onClick={() => {
                          void (async () => {
                            setReprocessBusy(w.id);
                            try {
                              const r = await superadminBillingService.replayPaymentWebhook(w.id);
                              toast({
                                title: r.replayed ? 'Replayed' : 'Handler reported failure',
                                description: w.eventName,
                              });
                              await loadWebhooks();
                            } catch {
                              toast({
                                title: 'Error',
                                description: 'Reprocess failed',
                                variant: 'destructive',
                              });
                            } finally {
                              setReprocessBusy(null);
                            }
                          })();
                        }}
                      >
                        Reprocess
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    </>
  );
};

export default SuperadminTenantPaymentsPage;
